<template>
  <div
    class="group bg-white rounded-md shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary/30 animate-scale-in course-card flex flex-col h-full"
    @click="$emit('navigate', course.slug)">
    <!-- PRICING SECTION - UPDATED FOR ALL-ACCESS -->
    <div class="pricing-section bg-gray-50">
      <!-- Single Row: Price and Status aligned -->
      <div class="flex items-center justify-between px-4 py-4">
        <!-- Left: Price Display -->
        <div class="flex items-baseline gap-2 flex-wrap">
          <!-- Subscription courses: Show crossed-out original price ONLY -->
          <span
            v-if="course.is_subscription && course.price > 0"
            class="text-2xl font-bold text-gray-400 line-through">
            {{
              castCurrency(course.price, course.currency_code || course.currency?.value || 'USD')
            }}
          </span>

          <!-- Non-subscription courses with promotion: show strikethrough + new price -->
          <template v-else-if="course.hasPromotion && course.price > 0">
            <span class="text-base font-medium text-gray-400 line-through">
              {{
                castCurrency(course.price, course.currency_code || course.currency?.value || 'USD')
              }}
            </span>
            <span class="text-2xl font-bold text-primary">
              {{
                castCurrency(
                  course.displayPrice,
                  course.currency_code || course.currency?.value || 'USD',
                )
              }}
            </span>
          </template>

          <!-- Free courses -->
          <span
            v-else-if="!course.displayPrice || course.displayPrice === 0"
            class="text-2xl font-bold text-success">
            FREE
          </span>

          <!-- Regular paid courses -->
          <span v-else class="text-2xl font-bold text-primary">
            {{
              castCurrency(
                course.displayPrice,
                course.currency_code || course.currency?.value || 'USD',
              )
            }}
          </span>
        </div>

        <!-- Right: EU Badge + Status Badge (vertically centered with price) -->
        <div class="flex items-center gap-2">
          <!-- EU Badge - only show for non-subscription courses -->
          <span
            v-if="
              (course.currency_code || course.currency?.value) &&
              (course.currency_code || course.currency?.value) !== 'USD' &&
              (course.displayPrice || course.price) > 0 &&
              !course.is_subscription
            "
            class="bg-primary text-white px-2.5 py-1 rounded text-xs font-semibold whitespace-nowrap">
            🌍 EU Exclusive
          </span>

          <!-- Status Badges -->
          <div
            v-if="course.isEnrolled"
            class="bg-success text-white px-3 py-1 rounded text-xs font-semibold whitespace-nowrap">
            Enrolled
          </div>
          <div
            v-else-if="course.is_subscription"
            class="all-access-badge-small px-3 py-1 rounded text-xs font-semibold whitespace-nowrap text-white">
            ✓ Included with All-Access
          </div>
          <div
            v-else-if="!course.displayPrice || course.displayPrice === 0"
            class="bg-gray-100 text-gray-600 px-3 py-1 rounded text-xs font-medium whitespace-nowrap">
            no payment required
          </div>
          <div
            v-else
            class="bg-gray-100 text-gray-600 px-3 py-1 rounded text-xs font-medium whitespace-nowrap">
            one-time payment
          </div>
        </div>
      </div>
    </div>

    <!-- Promo Banner - Full Width -->
    <div
      v-if="course.hasPromotion"
      class="gradient-background text-white text-center text-sm font-semibold py-2.5 px-4">
      {{ course.promoText }}
    </div>

    <!-- Main Content Area - Flex Grow -->
    <div class="flex-1 flex flex-col">
      <!-- Course Title -->
      <h3 class="text-primary text-2xl font-bold px-4 mb-2 mt-6 leading-tight">
        {{ course.name }}
      </h3>

      <!-- Course Description -->
      <div class="p-4 cursor-pointer flex-1">
        <p
          class="text-md text-gray-600 mb-4 leading-relaxed truncated-description"
          v-html="truncateDescription(course.description)"></p>

        <!-- Course Stats -->
        <div class="pt-4">
          <p class="flex items-center justify-start space-x-2 text-sm">
            <span
              class="inline-block px-3 py-1 bg-primary border border-primary text-white rounded-md font-normal">
              {{ getPodCount() }}
            </span>
            <span
              class="inline-block px-3 py-1 bg-primary border border-primary text-white rounded-md font-normal">
              {{ getLessonCount() }}
            </span>
            <span
              class="inline-block px-3 py-1 bg-primary border border-primary text-white rounded-md font-normal">
              {{ getEnrollmentCount() }}
            </span>
          </p>
        </div>

        <!-- Trainer Avatar Section -->
        <div
          v-if="course.trainers && course.trainers.length > 0"
          class="flex justify-center items-start space-x-4 py-4 mt-4">
          <div
            v-for="(trainer, index) in course.trainers"
            :key="index"
            class="flex flex-col items-center text-center">
            <img
              v-if="trainer.user && trainer.user.avatar_url"
              :src="trainer.user.avatar_url"
              class="avatar mb-2"
              :alt="trainer.user.name ? `${trainer.user.name}'s avatar` : 'Trainer avatar'" />
            <img v-else src="/images/default_post.png" class="avatar mb-2" alt="Default Avatar" />
            <div class="text-sm min-h-[3rem] flex flex-col justify-start items-center">
              <div v-if="trainer.user && trainer.user.name" class="font-medium text-gray-800">
                {{ trainer.user.name }}
              </div>
              <div v-if="trainer.user && trainer.user.title" class="text-gray-600 mb-1">
                {{ trainer.user.title }}
              </div>
              <a
                v-if="trainer.user && trainer.user.linkedin_url"
                :href="trainer.user.linkedin_url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-primary hover:text-primary-dark transition-colors duration-200"
                :title="trainer.user.name ? `View ${trainer.user.name}'s LinkedIn Profile` : 'View LinkedIn Profile'"
                :aria-label="trainer.user.name ? `View ${trainer.user.name}'s LinkedIn Profile` : 'View LinkedIn Profile'">
                <svg
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                    clip-rule="evenodd"></path>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tags -->
    <div v-if="filteredTags.length > 0" class="flex flex-wrap gap-2 px-4 pb-4">
      <span
        v-for="(tag, index) in filteredTags"
        :key="index"
        class="px-3 py-2 bg-primary-dark text-white text-xs rounded-md"
        :title="tag.tag_name || tag.name || tag">
        {{ tag.tag_name || tag.name || tag }}
      </span>
    </div>

    <!-- STICKY FOOTER -->
    <div class="card-footer-sticky bg-white px-4 pb-6 pt-4 border-t border-gray-100">
      <Link
        :href="getRoute('page.course.show', course.slug)"
        class="w-full bg-primary hover:bg-primary-dark hover:text-white hover:no-underline text-white py-3 rounded-lg font-semibold transition-colors duration-200 transform hover:scale-[1.02] text-center block"
        @click.stop>
        View Course
      </Link>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue'
  import { Link } from '@inertiajs/vue3'
  import { castCurrency } from '@/Services/castCurrency.js'
  import config from '@config'

  const props = defineProps({
    course: {
      type: Object,
      required: true,
    },
    courseImageUrl: {
      type: String,
      default: null,
    },
  })

  const emit = defineEmits(['navigate'])

  // Get promotion for this specific course
  const coursePromo = computed(() => {
    if (!config.course_promotions?.enabled) return null

    const promo = config.course_promotions.courses?.[props.course.id]

    if (!promo || !promo.enabled) return null

    // Check if promotion has expired
    if (promo.expires_at) {
      const expiryDate = new Date(promo.expires_at)
      if (expiryDate < new Date()) return null
    }

    return promo
  })

  // Enhance course object with promotion data
  const enhancedCourse = computed(() => {
    const promo = coursePromo.value

    return {
      ...props.course,
      hasPromotion: !!promo,
      displayPrice: promo?.promo_price_usd || props.course.price,
      promoText: promo?.promo_text || '',
    }
  })

  // Use enhanced course instead of props.course in template
  const course = enhancedCourse

  // Computed property to filter out null/undefined tags
  const filteredTags = computed(() => {
    if (!props.course.tagged || !Array.isArray(props.course.tagged)) {
      return []
    }

    return props.course.tagged.filter(
      (tag) => tag && (tag.tag_name || tag.name || (typeof tag === 'string' && tag.trim())),
    )
  })

  // Route helper function
  const getRoute = (name, params = {}) => {
    const routes = {
      'courses.show': `/dashboard/courses/${params}`,
      'page.course.show': `/page/courses/${params}`,
      'page.courses': '/page/courses',
    }
    return routes[name] || '/'
  }

  // Utility functions
  const getPodCount = () => {
    const podCount = props.course.pods?.length || 0
    return `${podCount} ${podCount === 1 ? 'Pod' : 'Pods'}`
  }

  const getLessonCount = () => {
    if (!props.course.pods || !Array.isArray(props.course.pods)) {
      return '0 Lessons'
    }

    const totalLessons = props.course.pods.reduce((total, pod) => {
      const lessonCount = pod.lesson?.length || 0
      return total + lessonCount
    }, 0)

    return `${totalLessons} ${totalLessons === 1 ? 'Lesson' : 'Lessons'}`
  }

  const getEnrollmentCount = () => {
    const count = props.course.enrollments?.length || props.course.enrollment_count || 0
    return count.toLocaleString() + ' Enrolled'
  }

  const truncateDescription = (description) => {
    if (!description) return ''
    // Let CSS handle truncation with line-clamp
    return description
  }
</script>

<style scoped>
  /* Card Styling */
  .course-card {
    cursor: pointer;
    transition:
      transform 0.2s ease-in-out,
      box-shadow 0.2s ease-in-out;
  }

  .course-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  /* Pricing Section */
  .pricing-section {
    min-height: 64px;
  }

  .pricing-section .flex.items-center {
    align-items: center;
  }

  /* Price Wrapper - prevents wrapping of prices */
  .price-wrapper {
    flex-shrink: 1;
    min-width: 0;
  }

  .whitespace-nowrap {
    white-space: nowrap;
  }

  /* Responsive Pricing */
  @media (max-width: 640px) {
    .pricing-section {
      min-height: auto;
    }

    .price-wrapper {
      flex-wrap: nowrap;
      overflow: hidden;
    }

    /* Ensure prices don't get too small */
    .text-xl {
      font-size: 1.25rem;
    }
  }

  /* Sticky Footer */
  .card-footer-sticky {
    margin-top: auto;
  }

  /* Truncated Description */
  .truncated-description {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 4.5rem;
    line-height: 1.5;
  }

  /* All-Access gradient badge (small version for cards) */
  .all-access-badge-small {
    background: linear-gradient(to right, #514a9d, #0db4f3);
    border: none;
  }

  /* Gradient Background for Promo */
  .gradient-background {
    background: linear-gradient(to right, #514a9d, #ff4242, #0db4f3);
    background-size: 300% 100%;
  }

  /* Avatar */
  .avatar {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid rgb(228, 231, 237);
  }

  /* Animations */
  @keyframes scaleIn {
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  .animate-scale-in {
    animation: scaleIn 0.2s ease-out;
  }

  /* Primary Colors */
  .bg-primary {
    background-color: #009ddb;
  }
  .bg-primary-dark {
    background-color: #007bb5;
  }
  .text-primary {
    color: #009ddb;
  }
  .text-primary-dark {
    color: #007bb5;
  }
  .border-primary {
    border-color: #009ddb;
  }
  .border-primary\/30 {
    border-color: rgba(0, 157, 219, 0.3);
  }

  /* Success Colors */
  .bg-success {
    background-color: #10b981;
  }
  .text-success {
    color: #10b981;
  }

  /* Hover States */
  .hover\:bg-primary-dark:hover {
    background-color: #007bb5;
  }
  .hover\:text-primary-dark:hover {
    color: #007bb5;
  }
  .hover\:scale-\[1\.02\]:hover {
    transform: scale(1.02);
  }
</style>
